import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role === "admin") {
        navigate("/admin");
      } else {
        setErr("Access denied: You are not authorized as an administrator.");
      }
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2A0A0E] flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Premium Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#6B1A24]/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#E69500]/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-[#6B1A24] border border-[#E69500]/30 rounded-2xl flex items-center justify-center shadow-lg">
            <ShieldAlert className="w-6 h-6 text-[#E69500]" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-heading font-extrabold text-white tracking-tight">
          Admin Console
        </h2>
        <p className="mt-2 text-center text-sm text-[#FDBABF]/80">
          Sign in to manage Hamarsathi
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-[#3E1016]/95 border border-[#6B1A24] py-8 px-4 shadow-xl rounded-2xl sm:px-10 backdrop-blur-sm">
          <form className="space-y-6" onSubmit={submit}>
            <div>
              <Label htmlFor="email" className="text-white text-xs uppercase tracking-wider">
                Admin Email Address
              </Label>
              <div className="mt-1.5">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-[#2A0A0E]/50 border-[#6B1A24] text-white focus:border-[#E69500] focus:ring-[#E69500] placeholder-[#FDBABF]/35"
                  placeholder="admin@hamarsathi.com"
                  data-testid="admin-login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-white text-xs uppercase tracking-wider">
                Password
              </Label>
              <div className="mt-1.5">
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#2A0A0E]/50 border-[#6B1A24] text-white focus:border-[#E69500] focus:ring-[#E69500] placeholder-[#FDBABF]/35"
                  placeholder="••••••••"
                  data-testid="admin-login-password"
                />
              </div>
            </div>

            {err && (
              <div className="p-3 bg-red-950/40 border border-red-800 rounded-xl text-xs text-red-200" data-testid="admin-login-error">
                {err}
              </div>
            )}

            <div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-full shadow-sm text-sm font-semibold text-white bg-[#6B1A24] hover:bg-[#52141C] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E69500] btn-press transition-colors"
                data-testid="admin-login-submit"
              >
                {loading ? "Signing in..." : "Sign in to console"}
              </Button>
            </div>
          </form>

          <div className="mt-6 border-t border-[#6B1A24]/40 pt-6 flex justify-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-xs text-[#FDBABF] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to user login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
